/**
 * @fileoverview Hook for initializing Sentry
 *
 * @example
 * // Usage:
 * import { useSentry } from './useSentry'
 *
 * function Component() {
 *   useSentry()
 *   // ...
 * }
 */

import * as Sentry from '@sentry/browser'

/**
 * Connection isn't yet part of the standard, but it is supported by Chrome and Firefox.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/connection
 */
/**
 * @interface Connection
 * @description The connection object
 * @property {object} connection - The connection object
 * @property {number} downlink - The downlink speed
 * @property {number} rtt - The round-trip time
 * @property {string} type - The connection type
 */
interface Connection {
    connection: {
        downlink: number
        rtt: number
        type: string
    }
}

/**
 * @function useSentry
 * @description Hook for initializing Sentry
 * @returns {void}
 */
export function useSentry(): void {
    // Prevent Sentry from being initialized multiple times in development
    if (['production', 'stage', 'test'].indexOf(process.env.SENTRY_ENV) > -1) {
        Sentry.init({
            dsn: 'https://b5a60d6a153e4065b4c271cf4b6675d5@o369932.ingest.sentry.io/4504084002963456',
            integrations: [new Sentry.BrowserTracing(), new Sentry.Replay()],
            environment: process.env.SENTRY_ENV,
            tracesSampleRate: 1.0,
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
        })

        if ((navigator as Navigator & Connection).connection) {
            Sentry.setContext('Connection', {
                downlink: (navigator as Navigator & Connection).connection.downlink,
                rtt: (navigator as Navigator & Connection).connection.rtt,
                type: (navigator as Navigator & Connection).connection.type,
            })
        }
    }
}
